import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'

export interface QuestionOption {
    value: string
    label: string
    carbon_factor?: number
}

export interface Question {
    id: number
    title: string
    description?: string
    type: 'single_choice' | 'multiple_choice' | 'number_input' | 'text_input'
    options?: QuestionOption[]
    unit?: string
    is_required: boolean
}

export interface QuestionnaireCategory {
    id: number
    name: string
    description?: string
    icon?: string
    questions: Question[]
}

// 问卷答案类型
export type QuestionAnswer = string | number | string[] | boolean

export interface QuestionnaireResponse {
    [questionId: number]: QuestionAnswer
}

// 推荐建议类型
export interface Recommendation {
    id: string
    title: string
    description: string
    impact: number
    difficulty: 'easy' | 'medium' | 'hard'
    category: string
}

// 抵消建议类型
export interface OffsetSuggestion {
    tons_to_offset: number
    estimated_cost: number
    projects: Array<{
        id: string
        name: string
        type: string
        price_per_ton: number
    }>
}

// 比较数据类型
export interface Comparison {
    average_footprint: number
    percentile: number
    category_breakdown: Record<string, number>
}

export interface CarbonFootprintResult {
    response_id: number
    total_carbon_footprint: number
    session_id: string
    category_breakdown?: Record<string, number>
    completed_at?: string
    recommendations?: Recommendation[]
    offset_suggestion?: OffsetSuggestion
    comparison?: Comparison
}

export const useQuestionnaireStore = defineStore('questionnaire', () => {
    const categories = ref<QuestionnaireCategory[]>([])
    const responses = ref<QuestionnaireResponse>({})
    const currentCategoryIndex = ref(0)
    const currentQuestionIndex = ref(0)
    const loading = ref(false)
    const sessionId = ref<string>('')
    const results = ref<CarbonFootprintResult | null>(null)

    const currentCategory = computed(() => {
        return categories.value[currentCategoryIndex.value] || null
    })

    const currentQuestion = computed(() => {
        if (!currentCategory.value) return null
        return currentCategory.value.questions[currentQuestionIndex.value] || null
    })

    const totalQuestions = computed(() => {
        return categories.value.reduce((total, category) => total + category.questions.length, 0)
    })

    const answeredQuestions = computed(() => {
        return Object.keys(responses.value).length
    })

    const progress = computed(() => {
        if (totalQuestions.value === 0) return 0
        return (answeredQuestions.value / totalQuestions.value) * 100
    })

    const isCurrentQuestionAnswered = computed(() => {
        if (!currentQuestion.value) return false
        return responses.value[currentQuestion.value.id] !== undefined
    })

    const canGoNext = computed(() => {
        if (!currentQuestion.value) return false
        if (currentQuestion.value.is_required && !isCurrentQuestionAnswered.value) {
            return false
        }
        return true
    })

    const isLastQuestion = computed(() => {
        if (!currentCategory.value) return true
        const isLastInCategory = currentQuestionIndex.value >= currentCategory.value.questions.length - 1
        const isLastCategory = currentCategoryIndex.value >= categories.value.length - 1
        return isLastInCategory && isLastCategory
    })

    // 获取问卷数据
    const fetchQuestionnaire = async (locale = 'en') => {
        loading.value = true
        try {
            const response = await api.get<{ questionnaire: QuestionnaireCategory[] }>('/questionnaire', { params: { locale } })
            if (response.success) {
                categories.value = response.data.questionnaire
                // 生成会话ID
                sessionId.value = generateSessionId()
            }
        } catch (error) {
            console.error('Failed to fetch questionnaire:', error)
            throw error
        } finally {
            loading.value = false
        }
    }

    // 设置答案
    const setAnswer = (questionId: number, answer: QuestionAnswer) => {
        responses.value[questionId] = answer
    }

    // 获取答案
    const getAnswer = (questionId: number) => {
        return responses.value[questionId]
    }

    // 下一题
    const nextQuestion = () => {
        if (!currentCategory.value) return

        if (currentQuestionIndex.value < currentCategory.value.questions.length - 1) {
            // 当前分类还有下一题
            currentQuestionIndex.value++
        } else if (currentCategoryIndex.value < categories.value.length - 1) {
            // 下一个分类
            currentCategoryIndex.value++
            currentQuestionIndex.value = 0
        }
    }

    // 上一题
    const previousQuestion = () => {
        if (currentQuestionIndex.value > 0) {
            // 当前分类的上一题
            currentQuestionIndex.value--
        } else if (currentCategoryIndex.value > 0) {
            // 上一个分类的最后一题
            currentCategoryIndex.value--
            const prevCategory = categories.value[currentCategoryIndex.value]
            currentQuestionIndex.value = prevCategory.questions.length - 1
        }
    }

    // 跳转到指定问题
    const goToQuestion = (categoryIndex: number, questionIndex: number) => {
        if (categoryIndex >= 0 && categoryIndex < categories.value.length) {
            currentCategoryIndex.value = categoryIndex
            const category = categories.value[categoryIndex]
            if (questionIndex >= 0 && questionIndex < category.questions.length) {
                currentQuestionIndex.value = questionIndex
            }
        }
    }

    // 提交问卷
    const submitQuestionnaire = async () => {
        loading.value = true
        try {
            // 检查用户认证状态
            const authStore = useAuthStore()
            console.log('提交问卷 - 认证状态检查:', {
                isAuthenticated: authStore.isAuthenticated,
                hasToken: !!authStore.token,
                userId: authStore.user?.id,
                userEmail: authStore.user?.email,
                tokenPreview: authStore.token ? authStore.token.substring(0, 20) + '...' : 'null'
            })

            if (!authStore.isAuthenticated || !authStore.token) {
                console.warn('用户未认证，问卷数据将作为匿名提交')
            }

            console.log('准备提交问卷数据:', {
                responses: responses.value,
                session_id: sessionId.value,
                responseCount: Object.keys(responses.value).length
            })

            const response = await api.post<CarbonFootprintResult>('/questionnaire/submit', {
                responses: responses.value,
                session_id: sessionId.value
            })

            console.log('问卷提交API响应:', {
                success: response.success,
                status: response.success ? 'success' : 'failed',
                data: response.data,
                message: response.message
            })

            if (response.success) {
                // 构造结果数据，确保包含所有必要字段
                const resultData = {
                    response_id: response.data.response_id,
                    total_carbon_footprint: response.data.total_carbon_footprint,
                    session_id: response.data.session_id,
                    category_breakdown: response.data.category_breakdown || {},
                    completed_at: response.data.completed_at
                }

                results.value = resultData

                console.log('问卷提交成功，结果数据:', resultData)

                // 如果用户已登录，标记问卷已完成并更新用户信息
                const authStore = useAuthStore()
                if (authStore.isAuthenticated && authStore.user) {
                    // 额外保存碳足迹分数到localStorage，作为备份
                    localStorage.setItem(`carbon_footprint_score_${authStore.user.id}`, response.data.total_carbon_footprint.toString())

                    // 标记问卷已完成，并传递碳足迹数据
                    await authStore.markQuestionnaireCompleted({
                        total_carbon_footprint: response.data.total_carbon_footprint,
                        category_breakdown: response.data.category_breakdown || {}
                    })

                    console.log('问卷提交成功，用户状态已更新', {
                        userId: authStore.user.id,
                        score: response.data.total_carbon_footprint,
                        breakdown: response.data.category_breakdown,
                        questionnaire_completed: authStore.user.questionnaire_completed
                    })
                }

                return resultData
            }
            throw new Error(response.message || 'Failed to submit questionnaire')
        } catch (error: any) {
            console.error('问卷提交失败 - 详细错误信息:', {
                error: error,
                message: error?.message,
                status: error?.status,
                type: error?.type,
                details: error?.details,
                stack: error?.stack
            })

            // 如果是认证错误，清理本地状态
            if (error?.status === 401 || error?.type === 'AUTH_ERROR') {
                console.warn('问卷提交失败：认证错误，清理本地认证状态')
                const authStore = useAuthStore()
                authStore.clearAuth()
            }

            throw error
        } finally {
            loading.value = false
        }
    }

    // 获取结果详情
    const fetchResults = async (responseId: number) => {
        loading.value = true
        try {
            const response = await api.get<CarbonFootprintResult>(`/questionnaire/results/${responseId}`)
            if (response.success) {
                results.value = { ...results.value, ...response.data }
                return response.data
            }
            throw new Error(response.message || 'Failed to fetch results')
        } catch (error) {
            console.error('Failed to fetch results:', error)
            throw error
        } finally {
            loading.value = false
        }
    }

    // 重置问卷
    const resetQuestionnaire = () => {
        responses.value = {}
        currentCategoryIndex.value = 0
        currentQuestionIndex.value = 0
        results.value = null
        sessionId.value = generateSessionId()
    }

    // 生成会话ID
    const generateSessionId = () => {
        return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    }

    return {
        // State
        categories,
        responses,
        currentCategoryIndex,
        currentQuestionIndex,
        loading,
        sessionId,
        results,

        // Computed
        currentCategory,
        currentQuestion,
        totalQuestions,
        answeredQuestions,
        progress,
        isCurrentQuestionAnswered,
        canGoNext,
        isLastQuestion,

        // Actions
        fetchQuestionnaire,
        setAnswer,
        getAnswer,
        nextQuestion,
        previousQuestion,
        goToQuestion,
        submitQuestionnaire,
        fetchResults,
        resetQuestionnaire,
    }
})